import React, { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import { Pencil, FileText } from "lucide-react";
import axiosInstance from "../../axiosInstance";
import GeneralTable from "../../components/Table/GeneralTable";
import StatusCard from "../../components/StatusCard/StatusCard";
import DetailsDrawer from "../../components/ui/DetailsDrawer";
import { ErrorState } from "../../components/ui/Feedback";
import { formatDateShort } from "./leaveUtils";
// Legacy stylesheet: kept (untouched) because it declares global selectors
// such as .edit-modal that other pages may still use.
import "./MyTimeSheet.scss";

const API = `${process.env.REACT_APP_API_EMPLOYEE_URL}/timesheets`;

const totalHours = (sheet) =>
  sheet.workTaskSheets.reduce(
    (total, task) => total + task.workedHours + task.workedMinutes / 60,
    0,
  );

const TaskList = ({ sheet, withHours = false }) => (
  <div className="ts-tasks">
    {sheet.workTaskSheets.map((task) => (
      <div key={task.workTaskId} className="ts-tasks__item">
        {task.projectName} - {task.task}
        {withHours ? ` - ${task.workedHours} hours` : ""}
      </div>
    ))}
  </div>
);

function MyTimeSheet() {
  const [timesheets, setTimesheets] = useState([]);
  const [allTimesheets, setAllTimesheets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [reportVisible, setReportVisible] = useState(false);
  const [loadingReport, setLoadingReport] = useState(false);
  const [reportError, setReportError] = useState(false);
  const [editing, setEditing] = useState(null); // draft being edited, or null
  const [saving, setSaving] = useState(false);

  const fetchLast7Days = useCallback(async () => {
    setLoadError(false);
    try {
      const response = await axiosInstance.get(`${API}/last-7-days`);
      setTimesheets(response.data.data || []);
    } catch (error) {
      console.error("Error fetching last 7 days timesheets", error);
      setLoadError(true);
    }
  }, []);

  const fetchAllTimesheets = useCallback(async () => {
    setLoadingReport(true);
    setReportError(false);
    try {
      const response = await axiosInstance.get(`${API}/All`);
      setAllTimesheets(response.data.data || []);
    } catch (error) {
      console.error("Error fetching all timesheets", error);
      setReportError(true);
    } finally {
      setLoadingReport(false);
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    fetchLast7Days().finally(() => setLoading(false));
  }, [fetchLast7Days]);

  const toggleReport = () => {
    const next = !reportVisible;
    setReportVisible(next);
    if (next) fetchAllTimesheets(); // fetch when the report is being revealed
  };

  const retryLast7Days = () => {
    setLoading(true);
    fetchLast7Days().finally(() => setLoading(false));
  };

  // ---- editing --------------------------------------------------------------
  const openEdit = (sheet) => {
    // Prepare the selected sheet for editing (workedMinutes holds the total
    // minutes, exactly as before).
    setEditing({
      ...sheet,
      workTaskSheets: sheet.workTaskSheets.map((task) => ({
        ...task,
        workedMinutes: task.workedHours * 60 + task.workedMinutes,
      })),
    });
  };

  const handleTaskChange = (e, taskIndex) => {
    const { name, value } = e.target;
    setEditing((prev) => {
      const tasks = [...prev.workTaskSheets];
      tasks[taskIndex] = { ...tasks[taskIndex], [name]: value };
      return { ...prev, workTaskSheets: tasks };
    });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (saving || !editing) return;

    const payload = {
      ...editing,
      // an edited timesheet goes back to the manager for approval
      managerApproval: "PENDING",
      workTaskSheets: editing.workTaskSheets.map((task) => ({
        ...task,
        workedHours: Number(task.workedHours) || 0,
        workedMinutes: Number(task.workedMinutes) || 0,
      })),
    };

    setSaving(true);
    try {
      await axiosInstance.put(`${API}/update`, payload);
      toast.success("Timesheet updated successfully");
      setEditing(null);
      // Refresh in place (this used to reload the whole page).
      await fetchLast7Days();
      if (reportVisible) fetchAllTimesheets();
    } catch (error) {
      console.error("Error updating timesheet", error);
      toast.error("Failed to update timesheet. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  // ---- tables ---------------------------------------------------------------
  const last7Rows = timesheets.map((sheet) => ({
    ...sheet,
    totalWorkedHours: totalHours(sheet).toFixed(2),
    workTasksText: sheet.workTaskSheets
      .map((task) => `${task.projectName} ${task.task}`)
      .join(" "),
  }));

  const reportRows = allTimesheets.map((sheet) => ({
    ...sheet,
    workTasksText: sheet.workTaskSheets
      .map((task) => `${task.projectName} ${task.task}`)
      .join(" "),
  }));

  const dateColumn = {
    header: "Timesheet Date",
    accessor: "timesheetDate",
    render: (row) => formatDateShort(row.timesheetDate),
  };

  const approvalColumn = {
    header: "Manager Approval",
    accessor: "managerApproval",
    render: (row) => <StatusCard status={row.managerApproval} />,
  };

  const last7Columns = [
    dateColumn,
    approvalColumn,
    {
      header: "Worked Hours",
      accessor: "totalWorkedHours",
      sortAccessor: (row) => Number(row.totalWorkedHours),
      render: (row) => `${row.totalWorkedHours} hours`,
    },
    {
      header: "Work Task",
      accessor: "workTasksText",
      render: (row) => <TaskList sheet={row} />,
    },
    {
      header: "Action",
      key: "action",
      sortable: false,
      align: "right",
      render: (row) => (
        <button
          type="button"
          className="hr-btn hr-btn--secondary ts-edit-btn"
          aria-label={`Edit timesheet for ${formatDateShort(row.timesheetDate)}`}
          onClick={() => openEdit(row)}
        >
          <Pencil size={14} aria-hidden="true" />
          Edit
        </button>
      ),
    },
  ];

  const reportColumns = [
    dateColumn,
    approvalColumn,
    {
      header: "Work Tasks",
      accessor: "workTasksText",
      render: (row) => <TaskList sheet={row} withHours />,
    },
  ];

  return (
    <div className="my-timesheet-page">
      {loadError ? (
        <section className="hr-card">
          <ErrorState
            title="Couldn't load your timesheets"
            message="Check your connection and try again."
            onRetry={retryLast7Days}
          />
        </section>
      ) : (
        <GeneralTable
          columns={last7Columns}
          data={last7Rows}
          loading={loading}
          rowKey={(row, index) => row.timeSheetId ?? index}
          title="Last 7 Days Timesheets"
          titleActions={
            <button
              type="button"
              className="hr-btn hr-btn--secondary"
              aria-pressed={reportVisible}
              onClick={toggleReport}
            >
              <FileText size={16} aria-hidden="true" />
              {reportVisible ? "Hide report" : "Show report"}
            </button>
          }
          emptyStateTitle="No timesheets in the last 7 days"
          emptyStateSubtitle="Timesheets you submit will appear here."
        />
      )}

      {reportVisible &&
        (reportError ? (
          <section className="hr-card">
            <ErrorState
              title="Couldn't load the timesheet report"
              message="Check your connection and try again."
              onRetry={fetchAllTimesheets}
            />
          </section>
        ) : (
          <GeneralTable
            columns={reportColumns}
            data={reportRows}
            loading={loadingReport}
            rowKey={(row, index) => row.timeSheetId ?? index}
            title="Full Timesheet Report"
            emptyStateTitle="No timesheets yet"
            emptyStateSubtitle="Your full timesheet history will be listed here."
          />
        ))}

      <DetailsDrawer
        open={Boolean(editing)}
        onClose={() => (saving ? undefined : setEditing(null))}
        title="Edit timesheet"
        footer={
          <>
            <button
              type="button"
              className="hr-btn hr-btn--secondary"
              onClick={() => setEditing(null)}
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="submit"
              form="timesheet-edit-form"
              className="hr-btn hr-btn--primary"
              disabled={saving}
            >
              {saving ? "Saving…" : "Save Changes"}
            </button>
          </>
        }
      >
        {editing && (
          <form id="timesheet-edit-form" onSubmit={handleSave}>
            <p className="ts-edit-date">
              {formatDateShort(editing.timesheetDate)} — saving sends this
              timesheet back to your manager for approval.
            </p>

            {editing.workTaskSheets.map((task, index) => (
              <fieldset key={task.workTaskId ?? index} className="ts-edit-task">
                <legend>Task {index + 1}</legend>

                <div className="ts-field">
                  <label htmlFor={`ts-${index}-project`}>Project name</label>
                  <input
                    id={`ts-${index}-project`}
                    type="text"
                    name="projectName"
                    className="hr-input"
                    value={task.projectName}
                    onChange={(e) => handleTaskChange(e, index)}
                  />
                </div>

                <div className="ts-field">
                  <label htmlFor={`ts-${index}-task`}>Task</label>
                  <input
                    id={`ts-${index}-task`}
                    type="text"
                    name="task"
                    className="hr-input"
                    value={task.task}
                    onChange={(e) => handleTaskChange(e, index)}
                  />
                </div>

                <div className="ts-field-row">
                  <div className="ts-field">
                    <label htmlFor={`ts-${index}-hours`}>Worked hours</label>
                    <input
                      id={`ts-${index}-hours`}
                      type="number"
                      min="0"
                      name="workedHours"
                      className="hr-input"
                      value={task.workedHours}
                      onChange={(e) => handleTaskChange(e, index)}
                    />
                  </div>
                  <div className="ts-field">
                    <label htmlFor={`ts-${index}-minutes`}>
                      Worked minutes
                    </label>
                    <input
                      id={`ts-${index}-minutes`}
                      type="number"
                      min="0"
                      name="workedMinutes"
                      className="hr-input"
                      value={task.workedMinutes}
                      onChange={(e) => handleTaskChange(e, index)}
                    />
                  </div>
                </div>
                <p className="ts-hint">
                  Minutes starts as this task's total time (hours × 60 +
                  minutes).
                </p>
              </fieldset>
            ))}
          </form>
        )}
      </DetailsDrawer>
    </div>
  );
}

export default MyTimeSheet;
